/**
 * ItemQueryWorkspace — the one shared item list block (roadmap N-16, N-19).
 *
 * The item list, the Report Designer and the Item Pool Editor draw their
 * list with these parts over one useItemQueryWorkspace state, so the query
 * bar, the filters, the columns and the rows are the same everywhere:
 *
 *   ItemQueryBar      the statement bar (Ask AI, Fix with AI) and, in builder
 *                     mode, the filters as chips
 *   ItemQueryRows     the filter panel beside whatever holds the rows
 *   ItemQueryTable    the item list's table over the block's rows, columns,
 *                     sort and selection — ranked for a pool
 *   ItemQueryFiltersButton, ItemQueryColumnsButton
 *                     Filters and Columns, for a host without the item
 *                     list's toolbar (the item list's toolbar draws its own
 *                     over the same state)
 *
 * The parts are separate because each host frames them differently — the
 * item list puts the bar under its toolbar and the rows in its body; the
 * designer puts charts between them; the pool puts the rows in its ranked
 * card. The state is one.
 */

import type { ComponentProps, ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { ColumnsControl } from '../../../collection/ColumnPicker';
import { btnSecondary } from '../../../mono/primitives';
import type { ItemQueryWorkspaceState } from '../hooks/useItemQueryWorkspace';
import { QueryStatementBar } from './QueryStatementBar';
import type { QueryViewState } from './QueryResultActions';
import { ActiveFiltersBar } from './ActiveFiltersBar';
import { FilterPanel } from './FilterPanel';
import { ItemTable } from './ItemTable';

// ============================================================================
// The query bar
// ============================================================================

export function ItemQueryBar({
  ws,
  view,
  appliedReport,
  saveVerb,
  resultActions,
  openAskAiFirst,
}: {
  ws: ItemQueryWorkspaceState;
  /** The presentation a saved query keeps (sort, grouping, columns, chart). */
  view: QueryViewState;
  appliedReport?: { id: string; queryText: string } | null;
  saveVerb?: boolean;
  resultActions?: boolean;
  openAskAiFirst?: boolean;
}) {
  return (
    <>
      <QueryStatementBar
        statement={ws.statement}
        filteredCount={ws.filteredCount}
        totalCount={ws.totalCount}
        matchedItemIds={ws.matchedItemIds}
        view={view}
        appliedReport={appliedReport}
        saveVerb={saveVerb}
        resultActions={resultActions}
        openAskAiFirst={openAskAiFirst}
      />
      {ws.statement.mode === 'builder' && (
        <ActiveFiltersBar
          filterGroups={ws.filterGroups}
          onRemoveRule={ws.removeRule}
          onRemoveGroup={ws.removeGroup}
          onClearAll={ws.clearFilters}
        />
      )}
    </>
  );
}

// ============================================================================
// The filter panel beside the rows
// ============================================================================

export function ItemQueryRows({
  ws,
  className = 'flex-1 flex overflow-hidden min-h-0',
  children,
}: {
  ws: ItemQueryWorkspaceState;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <FilterPanel
        isOpen={ws.filterPanelOpen && ws.statement.mode === 'builder'}
        onClose={() => ws.setFilterPanelOpen(false)}
        filterGroups={ws.filterGroups}
        onAddRule={ws.addRule}
        onRemoveRule={ws.removeRule}
        onRemoveGroup={ws.removeGroup}
        onSetConjunction={ws.setGroupConjunction}
        onAddGroup={ws.addGroup}
        onClearAll={ws.clearFilters}
      />
      {children}
    </div>
  );
}

// ============================================================================
// The rows
// ============================================================================

type ItemTableProps = ComponentProps<typeof ItemTable>;

/** What the host adds to the table: how a row opens, its acts, names, and any column override. */
type ItemQueryTableExtras = Omit<
  Partial<ItemTableProps>,
  | 'rows'
  | 'grouped'
  | 'selectedRows'
  | 'onToggleRow'
  | 'onSelectAll'
  | 'onSelectRows'
  | 'onClearSelection'
  | 'sort'
  | 'onSortChange'
  | 'expandedGroups'
  | 'onToggleGroup'
  | 'expandedClusters'
  | 'onToggleCluster'
> & { onRowClick: ItemTableProps['onRowClick'] };

export function ItemQueryTable({ ws, ...extras }: { ws: ItemQueryWorkspaceState } & ItemQueryTableExtras) {
  return (
    <ItemTable
      rows={ws.displayRows}
      grouped={ws.grouped}
      density={ws.density}
      selectedRows={ws.tableSelectedRows}
      onToggleRow={ws.handleToggleRow}
      onSelectAll={ws.handleSelectAllRows}
      onSelectRows={ws.handleSetRowsSelected}
      onClearSelection={ws.clearSelection}
      sort={ws.sort}
      onSortChange={ws.setSort}
      expandedGroups={ws.expandedGroups}
      onToggleGroup={ws.toggleGroup}
      expandedClusters={ws.expandedClusters}
      onToggleCluster={ws.toggleCluster}
      searchQuery={ws.searchQuery}
      queueNameMap={ws.queueNameMap}
      queues={ws.queues}
      visibleColumns={ws.visibleColumns}
      columnWidths={ws.columnWidths}
      onColumnResize={ws.setColumnWidth}
      onColumnResetWidth={ws.resetColumnWidth}
      {...extras}
    />
  );
}

// ============================================================================
// Filters and Columns, for hosts without the item list's toolbar
// ============================================================================

/** Filters: drawn only while the query can be shown as filters. */
export function ItemQueryFiltersButton({ ws }: { ws: ItemQueryWorkspaceState }) {
  if (!ws.filtersOffered) return null;
  return (
    <button
      type="button"
      {...btnSecondary(ws.filterPanelOpen ? 'border-[var(--slate)] text-[var(--text-pri)]' : '')}
      onClick={ws.toggleFilters}
    >
      <SlidersHorizontal className="h-3.5 w-3.5" />
      Filters
      {ws.statement.mode === 'builder' && ws.filterCount > 0 && (
        <span className="ml-1 rounded-full bg-[var(--slate)] px-1.5 py-0.5 text-[0.65rem] leading-none text-white">{ws.filterCount}</span>
      )}
    </button>
  );
}

export function ItemQueryColumnsButton({ ws }: { ws: ItemQueryWorkspaceState }) {
  return (
    <ColumnsControl
      columns={ws.allColumns.map((c) => ({ key: c.key, label: c.label }))}
      visibleColumns={ws.visibleColumns}
      onToggleColumn={ws.toggleColumn}
      onReorderColumns={ws.reorderColumns}
    />
  );
}
